import { ChangeReasonEnum, SalesPriceTypeCodeEnum } from '@inter-ikea-kompis/enums';
import type { IProductPriceInformation } from '@inter-ikea-kompis/types';

import type IProductPriceInformationSummary from './IProductPriceInformationSummary.js';

/**
 * Product price information summary transformer.
 */
export default class ProductPriceInformationSummaryTransformer {
	/**
	 * Extracts the information from the sales price of a product and puts it into properties that are easier to interpret.
	 *
	 * @param priceInformation Product price information.
	 * @param currency ...
	 * @returns Product price information summary.
	 */
	public static transform(
		priceInformation?: IProductPriceInformation,
		currency?: string
	): IProductPriceInformationSummary {
		const priceInformationSummary: IProductPriceInformationSummary = {
			temporaryFamilyPrice: false,
			newLowerPrice: false,
			hasPreviousPrice: false,
			timeRestrictedOffer: false,
			discontinued: false,
			familyPrice: null,
			price: null,
			regularPrice: null
		};

		if (!priceInformation?.salesPrice) {
			return priceInformationSummary;
		}

		const primaryCurrency = [
			...new Set(priceInformation.salesPrice.map((price) => price.currencyCode))
		][0];

		const salesPrices = currency
			? [...priceInformation.salesPrice]
					.filter((salesPrice) => salesPrice.currencyCode === currency)
					.reverse()
			: [...priceInformation.salesPrice]
					.filter((salesPrice) => salesPrice.currencyCode === primaryCurrency)
					.reverse();

		const isFamilyAndNLP = (): boolean => {
			const isFamily = salesPrices.some(
				(salesPrice) => salesPrice.typeCode === SalesPriceTypeCodeEnum.familySalesUnitPrice
			);

			const isNLP = salesPrices.some(
				(salesPrice) =>
					salesPrice.typeCode === SalesPriceTypeCodeEnum.regularSalesUnitPrice &&
					salesPrice.changeReason === ChangeReasonEnum.NewLowerPrice
			);

			return isFamily && isNLP;
		};

		const isFamilyAndNLPPrice = isFamilyAndNLP();

		for (const salesPrice of salesPrices) {
			if (salesPrice.typeCode === SalesPriceTypeCodeEnum.familySalesUnitPrice) {
				priceInformationSummary.familyPrice = salesPrice;
				if (salesPrice.changeReason === ChangeReasonEnum.Temporary) {
					priceInformationSummary.temporaryFamilyPrice = true;
				}
			} else if (salesPrice.changeReason === ChangeReasonEnum.NewLowerPrice) {
				priceInformationSummary.newLowerPrice = true;
				priceInformationSummary.price = salesPrice;
				if (salesPrice.previousSalesPrice?.priceInclTax) {
					priceInformationSummary.hasPreviousPrice = true;
					if (isFamilyAndNLPPrice) {
						priceInformationSummary.regularPrice = Object.assign({}, salesPrice);
					} else {
						priceInformationSummary.regularPrice = Object.assign(
							{},
							salesPrice,
							salesPrice.previousSalesPrice
						);
					}
				}
			} else if (salesPrice.changeReason === ChangeReasonEnum.TimeRestrictedOffer) {
				priceInformationSummary.timeRestrictedOffer = true;
				priceInformationSummary.price = salesPrice;
				if (salesPrice.previousSalesPrice?.priceInclTax) {
					priceInformationSummary.hasPreviousPrice = true;
					priceInformationSummary.regularPrice = Object.assign(
						{},
						salesPrice,
						salesPrice.previousSalesPrice
					);
				}
			} else if (priceInformation.salesPrice.length > 1) {
				priceInformationSummary.regularPrice = salesPrice;
			} else {
				priceInformationSummary.price = salesPrice;
			}

			if (salesPrice.changeReason === ChangeReasonEnum.Discontinued) {
				priceInformationSummary.discontinued = true;
			}
		}

		if (!priceInformationSummary.price && priceInformationSummary.regularPrice) {
			priceInformationSummary.price = priceInformationSummary.regularPrice;
		}

		return priceInformationSummary;
	}
}
